import {organizationContext,organizationRequestPath} from "./utils/organizationContext.js";
import {collectListingNeighborhoods} from './utils/listingGeography.js';
const API_ROOT = "/api";
const REQUEST_TIMEOUT_MS = 30_000;
let fallbackToken = "";
let sessionRevision = 0;
let storageUnavailable = false;

// Private browsing and blocked storage must not prevent signing in or out.
function withTokenStorage(callback) {
  if (storageUnavailable) return undefined;
  try {
    const storage = globalThis.localStorage;
    if (!storage) {
      storageUnavailable = true;
      return undefined;
    }
    return callback(storage);
  } catch {
    storageUnavailable = true;
    return undefined;
  }
}

export const tokenStore = {
  get() {
    const stored = withTokenStorage((storage) => storage.getItem("mre_token"));
    if (stored !== undefined && fallbackToken !== (stored || "")) { fallbackToken = stored || ""; sessionRevision++; }
    return fallbackToken;
  },
  set(token) {
    if (!token) return;
    fallbackToken = String(token);
    sessionRevision++;
    organizationContext.clear(sessionRevision,false);
    withTokenStorage((storage) => storage.setItem("mre_token", fallbackToken));
    if(typeof window!=="undefined")window.dispatchEvent(new Event("maywade-auth-changed"));
  },
  version() { this.get(); return sessionRevision; },
  clear() {
    sessionRevision++;
    fallbackToken = "";
    organizationContext.clear(sessionRevision,false);
    withTokenStorage((storage) => storage.removeItem("mre_token"));
    if(typeof window!=="undefined")window.dispatchEvent(new Event("maywade-auth-changed"));
  },
};

const queryString = (query = {}) => {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") params.set(key, value);
  });
  const text = params.toString();
  return text ? `?${text}` : "";
};

export async function request(method, path, body, authenticated = true, options = {}) {
  const token = typeof options.tokenOverride === "string" ? options.tokenOverride : authenticated ? tokenStore.get() : "";
  const revision = tokenStore.version();
  const contextual=authenticated&&organizationRequestPath(path),context=organizationContext.current(revision),contextVersion=organizationContext.version(revision);
  const logoutResponse = /^\/auth\/logout(?:-all)?$/.test(path);
  const guardResponse = !logoutResponse && (authenticated && !!token || /^\/auth\/(?:login|register)$/.test(path));
  const encodedBody = body !== undefined ? JSON.stringify(body) : undefined;
  const controller = new AbortController();
  const { signal } = options;
  const timeoutMs = Number.isFinite(options.timeoutMs) && options.timeoutMs > 0 ? options.timeoutMs : REQUEST_TIMEOUT_MS;
  let timedOut = false;
  const cancel = () => controller.abort();
  if (signal?.aborted) cancel();
  else signal?.addEventListener("abort", cancel, { once: true });
  const timeout = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  let response;
  let payload = null;
  try {
    response = await fetch(`${API_ROOT}${path}`, {
      method,
      cache: authenticated || /\/auth\/(?:forgot-password|reset-password)$/.test(path) ? "no-store" : "default",
      referrerPolicy: "no-referrer",
      signal: controller.signal,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        ...(contextual&&context?{"X-Organization-Id":context._id}:{}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.usageKey ? { 'X-Usage-Key': options.usageKey } : {}),
      },
      ...(encodedBody !== undefined ? { body: encodedBody } : {}),
    });
    if (response.status !== 204) {
      try {
        payload = await response.json();
      } catch (cause) {
        if (controller.signal.aborted) throw cause;
        if (response.ok) {
          const error = new Error("The server returned an invalid response. Please try again.", { cause });
          error.code = "INVALID_RESPONSE";
          error.status = response.status;
          throw error;
        }
      }
    }
  } catch (cause) {
    if (controller.signal.aborted) {
      const error = new Error(timedOut ? "The request timed out. Please try again." : "The request was cancelled.", { cause });
      error.name = timedOut ? "TimeoutError" : "AbortError";
      error.code = timedOut ? "REQUEST_TIMEOUT" : "REQUEST_ABORTED";
      throw error;
    }
    if (cause?.code === "INVALID_RESPONSE") throw cause;
    const error = new Error("Unable to connect. Check your connection and try again.", { cause });
    error.code = "NETWORK_ERROR";
    throw error;
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener("abort", cancel);
  }
  if (guardResponse && tokenStore.version() !== revision) {
    const error = new Error("Your sign-in changed while the request was running. Its response was discarded.");
    error.code="SESSION_CHANGED";throw error;
  }
  if(contextual&&organizationContext.version(tokenStore.version())!==contextVersion){const error=new Error("Your organization context changed. Reload before retrying.");error.code="ORGANIZATION_CHANGED";throw error;}
  if (!response.ok) {
    if (response.status === 401 && !logoutResponse && authenticated && token && tokenStore.get() === token) {
      tokenStore.clear();
      if (typeof window !== "undefined" && typeof window.dispatchEvent === "function" && typeof CustomEvent === "function") {
        window.dispatchEvent(new CustomEvent("mre:session-expired"));
      }
    }
    const message = [payload?.errorMessage, payload?.message].find((value) => typeof value === "string" && value.trim());
    const error = new Error(message || "Request failed");
    error.code = "HTTP_ERROR";
    error.status = response.status;
    error.payload = payload;
    throw error;
  }
  return payload;
}

export const api = {
  get: (path, query, authenticated = true, options) => request("GET", `${path}${queryString(query)}`, undefined, authenticated, options),
  post: (path, body, authenticated = true, options) => request("POST", path, body, authenticated, options),
  patch: (path, body, authenticated = true, options) => request("PATCH", path, body, authenticated, options),
  delete: (path, authenticated = true, options) => request("DELETE", path, undefined, authenticated, options),
};

async function meteredAiPost(path,payload) {
  let subject;
  try { subject=JSON.parse(atob(tokenStore.get().split('.')[1].replace(/-/g,'+').replace(/_/g,'/'))).jti; } catch { throw new Error('Sign in before using this action. / سجل الدخول قبل استخدام هذا الإجراء.'); }
  if(!subject)throw new Error('Sign in before using this action.');
  const bytes=await crypto.subtle.digest('SHA-256',new TextEncoder().encode(JSON.stringify([subject,path,payload])));
  const storageKey=`mre:ai-usage:${Array.from(new Uint8Array(bytes),value=>value.toString(16).padStart(2,'0')).join('')}`;
  let key;
  try {
    key=sessionStorage.getItem(storageKey)||crypto.randomUUID();
    sessionStorage.setItem(storageKey,key);
  }catch{throw new Error('Request recovery storage is unavailable. Your draft is unchanged. / تعذر حفظ مفتاح الاسترداد. لم تتغير مسودتك.');}
  const result=await api.post(path,payload,true,{usageKey:key});
  try{sessionStorage.removeItem(storageKey);}catch{/* Keep any surviving key for safe replay. */}
  return result;
}
export const endpoints = {
  agencyInquiry:(slug,input)=>api.post(`/profiles/agencies/${encodeURIComponent(slug)}/inquiries`,input),
  agencyQueue:(id,query)=>api.get(`/agency-work/organizations/${encodeURIComponent(id)}`,query),
  agencyAssignees:(id,query)=>api.get(`/agency-work/organizations/${encodeURIComponent(id)}/assignees`,query),
  agencyLead:(id,lead)=>api.get(`/agency-work/organizations/${encodeURIComponent(id)}/leads/${encodeURIComponent(lead)}`),
  agencyAssign:(id,lead,input)=>api.post(`/agency-work/organizations/${encodeURIComponent(id)}/leads/${encodeURIComponent(lead)}/assignment`,input),
  agencyHistory:(id,lead,query)=>api.get(`/agency-work/organizations/${encodeURIComponent(id)}/leads/${encodeURIComponent(lead)}/history`,query),
  agencyFollowUp:(id,lead,input)=>api.post(`/agency-work/organizations/${encodeURIComponent(id)}/leads/${encodeURIComponent(lead)}/follow-up`,input),
  agencyExport:(id,query)=>api.get(`/agency-work/organizations/${encodeURIComponent(id)}/export`,query),
  agencyCustomerDetail:lead=>api.get(`/agency-work/customer/${encodeURIComponent(lead)}`),
  agencyCustomerList:query=>api.get('/agency-work/customer',query),
  agencyConversation:(org,lead,query,options)=>api.get(org?`/agency-work/organizations/${encodeURIComponent(org)}/leads/${encodeURIComponent(lead)}/conversation`:`/agency-work/customer/${encodeURIComponent(lead)}/conversation`,query,true,options),
  agencySend:(org,lead,input)=>api.post(org?`/agency-work/organizations/${encodeURIComponent(org)}/leads/${encodeURIComponent(lead)}/messages`:`/agency-work/customer/${encodeURIComponent(lead)}/messages`,input),
  agencyViewed:(org,lead,cursor,proof)=>api.post(org?`/agency-work/organizations/${encodeURIComponent(org)}/leads/${encodeURIComponent(lead)}/viewed`:`/agency-work/customer/${encodeURIComponent(lead)}/viewed`,{cursor,proof}),
  agencyReviews:query=>api.get('/agency-work/review',query),
  agencyReview:(id,input)=>api.post(`/agency-work/review/${encodeURIComponent(id)}`,input),
  organizations: () => api.get('/organizations'),
  organization: id => api.get(`/organizations/${encodeURIComponent(id)}`),
  createOrganization: input => api.post('/organizations',input),
  organizationMembers: (id,query) => api.get(`/organizations/${encodeURIComponent(id)}/members`,query),
  organizationMember: (id,userId,input) => api.post(`/organizations/${encodeURIComponent(id)}/members/${encodeURIComponent(userId)}`,input),
  leaveOrganization: (id,input) => api.post(`/organizations/${encodeURIComponent(id)}/leave`,input),
  organizationInvitations: (id,query) => api.get(`/organizations/${encodeURIComponent(id)}/invitations`,query),
  inviteOrganizationMember: (id,input) => api.post(`/organizations/${encodeURIComponent(id)}/invitations`,input),
  changeOrganizationInvitation: (id,inviteId,input) => api.post(`/organizations/${encodeURIComponent(id)}/invitations/${encodeURIComponent(inviteId)}`,input),
  pendingOrganizationInvitations: () => api.get('/organizations/invitations'),
  acceptOrganizationInvitation: input => api.post('/organizations/accept',input),
  organizationHistory: (id,query) => api.get(`/organizations/${encodeURIComponent(id)}/history`,query),
  organizationProjects: (id,query) => api.get(`/organizations/${encodeURIComponent(id)}/projects`,query),
  createProject: input => api.post('/projects',input),
  professionalMine: () => api.get('/professional'),
  professionalCommand: (role,input) => api.post(`/professional/${encodeURIComponent(role)}`,input),
  professionalUpload: (role,input) => api.post(`/professional/${encodeURIComponent(role)}/evidence`,input),
  professionalContent: (id,docId) => api.get(`/professional/applications/${encodeURIComponent(id)}/evidence/${encodeURIComponent(docId)}/content`),
  professionalQueue: query => api.get('/professional/review',query),
  professionalDetail: id => api.get(`/professional/applications/${encodeURIComponent(id)}`),
  professionalDecision: (id,input) => api.post(`/professional/applications/${encodeURIComponent(id)}/decision`,input),
  register: (payload) => api.post("/auth/register", payload, false),
  login: (payload) => api.post("/auth/login", payload, false),
  logout: () => api.post("/auth/logout", {}),
  logoutAll: () => api.post("/auth/logout-all", {}),
  sendLogout: (token, scope) => api.post(scope === "all" ? "/auth/logout-all" : "/auth/logout", {}, true, {tokenOverride:token}),
  sessions: (query={}) => api.get("/auth/sessions", query),
  refresh: () => api.post("/auth/refresh", {}),
  forgotPassword: (payload) => api.post("/auth/forgot-password", payload, false),
  resetPassword: (payload) => api.post("/auth/reset-password", payload, false),
  me: () => api.get("/auth/me"),
  dashboardMe: () => api.get("/users/dashboard"),
  listings: (query = {}, options) => api.get("/listings", query, false, options),
  listing: (id, options) => api.get(`/listings/${id}`, {}, false, options),
  createListing: (payload) => api.post("/listings", payload),
  updateListing: (id, payload) => api.patch(`/listings/${encodeURIComponent(id)}`, payload),
  createListingDraft: payload => api.post('/listings/drafts',payload),
  recoverListingDraft: key => api.get(`/listings/drafts/${encodeURIComponent(key)}`),
  governorates: options => api.get('/areas/governorates',{},false,options),
  wilayats: (governorateId,options) => api.get('/areas/wilayats',{governorateId},false,options),
  listingNeighborhoods: (governorateId,wilayatId,options) => collectListingNeighborhoods(page=>api.get('/areas/neighborhoods',{governorateId,wilayatId,limit:100,page},false,options)),
  listingRevision: id => api.get(`/listings/${encodeURIComponent(id)}/revision`),
  submitListingReview: (id, version = {}) => api.post(`/listings/${encodeURIComponent(id)}/submit-review`, version),
  publishListing: (id, version = {}) => api.post(`/listings/${encodeURIComponent(id)}/publish`, version),
  unpublishListing: (id, version = {}) => api.post(`/listings/${encodeURIComponent(id)}/unpublish`, version),
  deleteListing: (id) => api.delete(`/listings/${id}`),
  listingAssets: id => api.get(`/listings/${encodeURIComponent(id)}/assets`),
  prepareListingUpload: (id,payload) => api.post(`/listings/${encodeURIComponent(id)}/media/prepare`,payload),
  cancelListingUpload: (id,key,payload) => api.post(`/listings/${encodeURIComponent(id)}/media/${encodeURIComponent(key)}/cancel`,payload),
  listingAssetContent: (id,asset) => api.get(`/listings/${encodeURIComponent(id)}/assets/${encodeURIComponent(asset)}/content`),
  detachListingAsset: (id,asset,payload) => api.post(`/listings/${encodeURIComponent(id)}/assets/${encodeURIComponent(asset)}/detach`,payload),
  listingPreview: id => api.get(`/listings/${encodeURIComponent(id)}/preview`),
  uploadListingMedia: (id, payload) => api.post(`/listings/${id}/media`, payload),
  createLead: (payload) => api.post("/leads", payload),
  naturalLanguageSearch: (payload) => api.post("/search/natural-language", payload, false),
  mapSearch: (payload) => api.post("/search/map-area", payload, false),
  saveSearch: (payload) => api.post("/search/save", payload),
  savedSearches: () => api.get("/search/saved"),
  updateSavedSearch: (id,payload) => api.patch(`/search/saved/${encodeURIComponent(id)}`,payload),
  matchSavedSearch: id => api.post(`/search/saved/${encodeURIComponent(id)}/match`,{}),
  savedSearchMatches: (id,query={}) => api.get(`/search/saved/${encodeURIComponent(id)}/matches`,query),
  notificationPreferences: () => api.get('/notifications/preferences'),
  saveNotificationPreferences: payload => api.patch('/notifications/preferences',payload),
  notificationOperations: (query={}) => api.get('/notifications/admin',query),
  dispatchNotification: id => api.post(`/notifications/admin/${encodeURIComponent(id)}/dispatch`,{}),
  removeSavedSearch: (id) => api.delete(`/search/saved/${id}`),
  area: (slug) => api.get(`/areas/neighborhoods/${slug}`, {}, false),
  neighborhoods: () => api.get("/areas/neighborhoods", {}, false),
  projects: () => api.get("/projects", {}, false),
  project: (id, options) => api.get(`/projects/${id}`, {}, false, options),
  registerProjectInterest: (id, payload) => api.post(`/projects/${id}/register-interest`, payload),
  investments: () => api.get("/investments", {}, false),
  investment: (id, options) => api.get(`/investments/${encodeURIComponent(id)}`, {}, false, options),
  myInvestmentInterest: (id) => api.get(`/investments/${encodeURIComponent(id)}/my-interest`),
  registerInvestmentInterest: (id, payload) => api.post(`/investments/${encodeURIComponent(id)}/register-interest`, payload),
  providers: (query = {}) => api.get("/services/providers", query, false),
  agents: () => api.get("/profiles/agents", {}, false),
  agencies: () => api.get("/profiles/agencies", {}, false),
  agency: (slug) => api.get(`/profiles/agencies/${slug}`, {}, false),
  developers: () => api.get("/profiles/developers", {}, false),
  developer: (slug) => api.get(`/profiles/developers/${slug}`, {}, false),
  createServiceRequest: (payload) => api.post("/services/requests", payload),
  requestQuotes: (id) => api.get(`/services/requests/${encodeURIComponent(id)}/quotes`),
  serviceJobs: (query={}) => api.get('/services/jobs',query),
  createServiceJob: quoteId => api.post(`/services/quotes/${encodeURIComponent(quoteId)}/job`,{}),
  serviceJob: id => api.get(`/services/jobs/${encodeURIComponent(id)}`),
  serviceJobAction: (id,payload) => api.post(`/services/jobs/${encodeURIComponent(id)}/actions`,payload),
  serviceJobHistory: (id,page=1) => api.get(`/services/jobs/${encodeURIComponent(id)}/history`,{page}),
  serviceJobUploads: id => api.get(`/services/jobs/${encodeURIComponent(id)}/uploads`),
  uploadServiceJobEvidence: (id,payload) => api.post(`/services/jobs/${encodeURIComponent(id)}/uploads`,payload),
  serviceJobEvidenceContent: (id,uploadId) => api.get(`/services/jobs/${encodeURIComponent(id)}/uploads/${encodeURIComponent(uploadId)}/content`),
  discardServiceJobUpload: (id,uploadId) => api.delete(`/services/jobs/${encodeURIComponent(id)}/uploads/${encodeURIComponent(uploadId)}`),
  myServiceQuotes: () => api.get("/services/quotes"),
  createServiceQuote: (id, payload) => api.post(`/services/requests/${encodeURIComponent(id)}/quotes`, payload),
  decideServiceQuote: (id, payload) => api.patch(`/services/quotes/${encodeURIComponent(id)}/status`, payload),
  plans: () => api.get("/plans", {}, false),
  selectPlan: (payload) => api.post("/plans/subscriptions/select", payload),
  currentSubscription: () => api.get("/plans/subscriptions/current"),
  currentUsage: () => api.get('/plans/subscriptions/usage'),
  correctUsage: (id,payload) => api.post(`/plans/admin/usage/${encodeURIComponent(id)}/corrections`,payload),
  usageOperation: id => api.get(`/plans/admin/usage/${encodeURIComponent(id)}`),
  blog: () => api.get("/blog", {}, false),
  blogPost: (slug) => api.get(`/blog/${slug}`, {}, false),
  page: (slug) => api.get(`/pages/${slug}`, {}, false),
  compareProperties: (listingIds, values = {}, options) => api.post("/comparisons/properties", { listingIds, save: false, ...values }, true, options),
  comparisons: (query = {}, options) => api.get("/comparisons", query, true, options),
  comparison: (id, options) => api.get(`/comparisons/${encodeURIComponent(id)}`, {}, true, options),
  removeComparison: (id) => api.delete(`/comparisons/${encodeURIComponent(id)}`),
  favorites: (query = {}, options) => api.get("/favorites", query, true, options),
  addFavorite: (payload) => api.post("/favorites", payload),
  removeFavorite: (targetType, targetId) => api.delete(`/favorites/${encodeURIComponent(targetType)}/${encodeURIComponent(targetId)}`),
  viewings: (query={}) => api.get('/appointments',query),
  viewing: id => api.get('/appointments/'+encodeURIComponent(id)),
  viewingAction: (id,input) => api.post('/appointments/'+encodeURIComponent(id)+'/actions',input),
  viewingHistory: (id,query={}) => api.get('/appointments/'+encodeURIComponent(id)+'/history',query),
  viewingAvailability: (id,query) => api.get('/appointments/'+encodeURIComponent(id)+'/availability',query),
  createAppointment: (payload) => api.post("/appointments", payload),
  offers: (query={}) => api.get('/offers',query),
  offer: id => api.get('/offers/'+encodeURIComponent(id)),
  offerAction: (id,input) => api.post('/offers/'+encodeURIComponent(id)+'/actions',input),
  offerHistory: (id,query={}) => api.get('/offers/'+encodeURIComponent(id)+'/history',query),
  createOffer: (payload) => api.post("/offers", payload),
  rentalApplications: (query={}) => api.get('/rental-applications',query),
  rentalApplication: id => api.get('/rental-applications/'+encodeURIComponent(id)),
  rentalAction: (id,input) => api.post('/rental-applications/'+encodeURIComponent(id)+'/actions',input),
  rentalHistory: (id,query={}) => api.get('/rental-applications/'+encodeURIComponent(id)+'/history',query),
  rentalUploads: (id,query={}) => api.get('/rental-applications/'+encodeURIComponent(id)+'/evidence',query),
  uploadRentalEvidence: (id,input) => api.post('/rental-applications/'+encodeURIComponent(id)+'/evidence',input),
  rentalEvidenceContent: (id,documentId) => api.get('/rental-applications/'+encodeURIComponent(id)+'/evidence/'+encodeURIComponent(documentId)+'/content'),
  cleanupRentalUpload: (id,uploadId) => api.post('/rental-applications/'+encodeURIComponent(id)+'/evidence/'+encodeURIComponent(uploadId)+'/cleanup',{}),
  createRentalApplication: (payload) => api.post("/rental-applications", payload),
  createConversation: (payload) => api.post("/conversations", payload),
  conversations: (query = {},options) => api.get("/conversations",query,true,options),
  conversation: (id,query = {},options) => api.get(`/conversations/${encodeURIComponent(id)}`,query,true,options),
  sendMessage: (id, payload,options) => api.post(`/conversations/${encodeURIComponent(id)}/messages`, payload,true,options),
  viewConversation: (id,cursor,proof) => api.post(`/conversations/${encodeURIComponent(id)}/viewed`,{cursor,proof}),
  publisherInbox:(query,options)=>api.get("/conversations/inbox",query,true,options),
  publisherThread:(id,options)=>api.get(`/conversations/inbox/${encodeURIComponent(id)}`,{},true,options),
  publisherMetrics:(query,options)=>api.get("/conversations/inbox/metrics",query,true,options),
  publisherChange:(id,payload)=>api.post(`/conversations/inbox/${encodeURIComponent(id)}/follow-up`,payload),
  inquiryLeads: (query = {},options) => api.get("/leads",query,true,options),
  inquiryStage: (id,status) => api.patch(`/leads/${encodeURIComponent(id)}/status`,{status}),
  kycMe: () => api.get("/kyc/me"),
  startKyc: () => api.post("/kyc/start", {}),
  uploadKyc: (payload) => api.post("/kyc/upload", payload),
  kycDocumentContent: (id) => api.get(`/kyc/documents/${id}/content`),
  wallet: () => api.get("/wallet"),
  walletTransactions: () => api.get("/wallet/transactions"),
  invoices: () => api.get("/invoices"),
  aiChat: (payload) => ['listing_description','property_photo_analysis'].includes(payload.taskType)?meteredAiPost('/ai/chat',payload):api.post('/ai/chat',payload),
  aiImproveListing: (payload) => meteredAiPost('/ai/listing/improve',payload),
  aiGenerateDescription: payload => meteredAiPost('/ai/listing-description',payload),
  proposeListingDescription: (payload,key) => api.post('/ai/listing-description/proposals',payload,true,{usageKey:key}),
  readListingDescriptionProposal: id => api.get(`/ai/listing-description/proposals/${encodeURIComponent(id)}`),
  applyListingDescription: (id,payload) => api.post(`/ai/listing-description/proposals/${encodeURIComponent(id)}/apply`,payload),
  discardListingDescription: id => api.delete(`/ai/listing-description/proposals/${encodeURIComponent(id)}`),
  aiAnalyzeImages: payload => meteredAiPost('/ai/property-image-analysis',payload),
  aiPropertySummary: (payload, options) => api.post("/ai/property/summary", payload, false, options),
  aiTranslateListing: (payload) => api.post("/ai/listing/translate", payload),
  aiRelocationAssistant: (payload) => api.post("/ai/relocation/assistant", payload, false),
  aiAdminLogs: (query = {}) => api.get("/ai/admin/logs", query),
  aiAdminAlerts: (query = {}) => api.get("/ai/admin/alerts", query),
  aiAdminSettings: (payload) => api.patch("/ai/admin/settings", payload),
  aiAdminTestModel: (payload) => api.post("/ai/admin/test-model", payload),
  workspaceList: (resource, query = {}) => api.get(`/workspace/resources/${resource}`, query),
  workspaceCreate: (resource, payload) => api.post(`/workspace/resources/${resource}`, payload),
  workspaceUpdate: (resource, id, payload) => api.patch(`/workspace/resources/${resource}/${id}`, payload),
  workspaceDelete: (resource, id) => api.delete(`/workspace/resources/${resource}/${id}`),
  inspectionBookings: (query={}) => api.get('/inspections/bookings',query),
  createInspectionBooking: payload => api.post('/inspections/bookings',payload),
  inspectionBooking: (id,reason='') => api.get(`/inspections/bookings/${encodeURIComponent(id)}`,{reason}),
  inspectionAction: (id,payload) => api.post(`/inspections/bookings/${encodeURIComponent(id)}/actions`,payload),
  inspectionVersions: (id,page=1,reason='') => api.get(`/inspections/bookings/${encodeURIComponent(id)}/versions`,{page,reason}),
  inspectionVersion: (id,versionId,reason='') => api.get(`/inspections/bookings/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}`,{reason}),
  inspectionUploads: (id,reason='') => api.get(`/inspections/bookings/${encodeURIComponent(id)}/uploads`,{reason}),
  uploadInspectionEvidence: (id,payload) => api.post(`/inspections/bookings/${encodeURIComponent(id)}/uploads`,payload),
  inspectionDraftEvidence: (id,uploadId,reason='') => api.get(`/inspections/bookings/${encodeURIComponent(id)}/uploads/${encodeURIComponent(uploadId)}/content`,{reason}),
  inspectionReleasedEvidence: (id,versionId,uploadId,reason='') => api.get(`/inspections/bookings/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}/uploads/${encodeURIComponent(uploadId)}/content`,{reason}),
  discardInspectionUpload: (id,uploadId) => api.delete(`/inspections/bookings/${encodeURIComponent(id)}/uploads/${encodeURIComponent(uploadId)}`),
  workspaceInspectionReport: (id, payload) => api.post(`/workspace/inspection-bookings/${id}/report`, payload),
  adminRecords: (collection, query = {}) => api.get(`/admin/records/${collection}`, query),
  adminCreateRecord: (collection, payload) => api.post(`/admin/records/${collection}`, payload),
  adminUpdateRecord: (collection, id, payload) => api.patch(`/admin/records/${collection}/${id}`, payload),
  adminDeleteRecord: (collection, id) => api.delete(`/admin/records/${collection}/${id}`),
  dashboard: () => api.get("/admin/dashboard"),
  government: () => api.get("/government/dashboard"),
};
